import React, { useState, useEffect } from 'react';
import Homepage from './components/Homepage';
import Collections from './components/Collections';
import Checkout from './components/Checkout';
import Payment from './components/Payment';
import NewArrivals from './components/NewArrivals';
import AdminPanel from './components/AdminPanel';
import SearchDrawer from './components/SearchDrawer';
import LegalPage from './components/LegalPage';
import Marketplace from './components/Marketplace';
import ProductPage from './components/ProductPage';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import {
  subscribeProducts,
  subscribeDiscounts,
  subscribeHomepageSettings,
  subscribeCollectionsSettings,
  firebaseEnabled
} from './utils/firebaseService';

function App() {
  const getInitialState = () => {
    const path = window.location.pathname.toLowerCase();
    if (path === '/admin') return { view: 'admin', title: 'Terms & Conditions' };
    if (path === '/shipping-policy') return { view: 'legal', title: 'Shipping Policy' };
    if (path === '/return-policy' || path === '/exchange-return-refund-policy' || path === '/refund-policy') return { view: 'legal', title: 'Exchange, Return & Refund Policy' };
    if (path === '/terms-and-conditions' || path === '/terms') return { view: 'legal', title: 'Terms & Conditions' };
    if (path === '/privacy-policy') return { view: 'legal', title: 'Privacy Policy' };
    return { view: 'home', title: 'Terms & Conditions' };
  };

  const initial = getInitialState();
  const [view, setView] = useState(initial.view);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [legalTitle, setLegalTitle] = useState(initial.title);
  const [selectedCollectionId, setSelectedCollectionId] = useState(0);
  const [favorites, setFavorites] = useState([false, false, false]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Cart item state
  const [cartItems, setCartItems] = useState([]);

  // Appointment Form States
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingForm, setBookingForm] = useState({
    boutique: 'Jaipur Heritage Flagship, C-Scheme',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
  });

  // Current user authentication state
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const saved = localStorage.getItem('hok_currentUser');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  // Connect real-time Firebase Auth listener
  useEffect(() => {
    if (firebaseEnabled) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          setCurrentUser(user);
          localStorage.setItem('hok_currentUser', JSON.stringify({
            uid: user.uid,
            displayName: user.displayName,
            email: user.email,
            photoURL: user.photoURL,
          }));
        } else {
          setCurrentUser(null);
          localStorage.removeItem('hok_currentUser');
        }
      });
      return unsubscribe;
    }
  }, []);

  // Lifted catalog, discounts, and homepage content states
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [discounts, setDiscounts] = useState([]);
  const [homeContent, setHomeContent] = useState({
    heroTitle: 'Wearable Heritage',
    heroSubtitle: 'Handcrafted Silver Jewellery from Jaipur',
    heroImage: '/hero_silver_jewelry.jpg',
    mobileHeroImage: '',
    calloutVideo: '',
  });
  const [collectionsData, setCollectionsData] = useState({
    1: {
      title: "Polki Heirlooms",
      banner: "/new_arrival_2.jpg",
      description: "Polki, long associated with India’s royal jewellery traditions, is revered for its raw uncut diamonds, timeless character, and heirloom significance. Drawing inspiration from this enduring artistry, House of Karnnikaraa reinterprets the essence of traditional Polki into silver jewellery — preserving the richness of its heritage while shaping it into a contemporary expression of timeless adornment."
    },
    2: {
      title: "Classic CZ",
      banner: "/new_arrival_1.jpg",
      description: "Cubic Zirconia, celebrated for its brilliance and refined clarity, brings a contemporary dimension to timeless jewellery design. At House of Karnnikaraa, it is thoughtfully interpreted through silver jewellery — balancing luminosity, detail, and modern sophistication within the house’s heritage-inspired design language."
    },
    3: {
      title: "Eternal Moissanite",
      banner: "/banner_silver_jewelry.jpg",
      description: "Moissanite, admired for its exceptional brilliance and enduring beauty, brings a contemporary radiance to fine jewellery traditions. At House of Karnnikaraa, it is reimagined through sterling silver into timeless creations that balance luminosity, elegance, and modern sophistication while remaining rooted in the house’s cultural design sensibility."
    },
    4: {
      title: "Royal Meenakari",
      banner: "/new_arrival_3.jpg",
      description: "Meenakari, one of Rajasthan’s most celebrated decorative art forms, is known for its intricate enamel work, vivid colours, and royal heritage. House of Karnnikaraa reinterprets this timeless craft through silver jewellery — preserving its ornamental richness and cultural artistry within a contemporary language of adornment."
    },
    5: {
      title: "Jaipur Blue Pottery",
      banner: "/blue_pottery_banner.jpg",
      description: "Blue Pottery, one of Jaipur’s most treasured artistic legacies, reflects the richness of Rajasthan’s cultural heritage through its intricate motifs and luminous blue glaze. House of Karnnikaraa stands amongst the only jewellery houses to reinterpret this iconic Jaipur craft into silver jewellery — transforming a timeless art form into collectible wearable heritage."
    },
    6: {
      title: "Minimalist Luxe",
      banner: "/hero_silver_jewelry.jpg",
      description: "Minimalist Luxe reflects a quieter expression of adornment — where clean forms, subtle detailing, and refined silver craftsmanship come together with timeless versatility. Thoughtfully designed for the modern woman, the collection balances understated elegance with House of Karnnikaraa’s enduring sensibility towards culture, beauty, and everyday sophistication."
    }
  });

  // Initialize and load dynamic database content in real-time
  useEffect(() => {
    // ONE-TIME CLEANUP: Remove pre-added products from localStorage
    try {
      const local = localStorage.getItem('hok_products');
      if (local) {
        let savedProducts = JSON.parse(local);
        // Default products have ids like 'ring-1', 'necklace-10', etc.
        const filteredProducts = savedProducts.filter(p => !/^(ring|earring|necklace|pendant|bracelet|set)-\d+$/.test(p.id));
        if (savedProducts.length !== filteredProducts.length) {
          localStorage.setItem('hok_products', JSON.stringify(filteredProducts));
          window.dispatchEvent(new CustomEvent('hok_products_updated', { detail: filteredProducts }));
        }
      }
    } catch (e) {}

    let unsubscribeProducts = () => {};
    const fetchLiveDbData = async () => {
      const defaultProducts = [];
      unsubscribeProducts = subscribeProducts((liveProducts) => {
        setProducts(liveProducts);
      }, defaultProducts);

      const defaultDiscounts = [
        { code: 'WELCOME10', type: 'percentage', value: 10, status: 'Active' },
        { code: 'FESTIVE20', type: 'percentage', value: 20, status: 'Active' },
        { code: 'KARNNIKARA5K', type: 'fixed', value: 5000, status: 'Active' }
      ];
      let unsubscribeDiscounts = () => {};
      unsubscribeDiscounts = subscribeDiscounts((liveDiscounts) => {
        setDiscounts(liveDiscounts);
      }, defaultDiscounts);

      let unsubscribeHome = () => {};
      let unsubscribeCollections = () => {};

      unsubscribeHome = subscribeHomepageSettings((liveHomeContent) => {
        if (liveHomeContent) {
          const normalized = {
            ...liveHomeContent,
            calloutVideo: liveHomeContent.calloutVideo === '/jewelry_video.mp4' ? '' : (liveHomeContent.calloutVideo || '')
          };
          setHomeContent(normalized);
        }
      }, {
        heroTitle: 'Wearable Heritage',
        heroSubtitle: 'Handcrafted Silver Jewellery from Jaipur',
        heroImage: '/hero_silver_jewelry.jpg',
        calloutVideo: '',
      });

      unsubscribeCollections = subscribeCollectionsSettings((liveCollectionsData) => {
        setCollectionsData(liveCollectionsData);
      }, {
        1: {
          title: "Polki Heirlooms",
          banner: "/new_arrival_2.jpg",
          description: "Polki, long associated with India’s royal jewellery traditions, is revered for its raw uncut diamonds, timeless character, and heirloom significance. Drawing inspiration from this enduring artistry, House of Karnnikaraa reinterprets the essence of traditional Polki into silver jewellery — preserving the richness of its heritage while shaping it into a contemporary expression of timeless adornment."
        },
        2: {
          title: "Classic CZ",
          banner: "/new_arrival_1.jpg",
          description: "Cubic Zirconia, celebrated for its brilliance and refined clarity, brings a contemporary dimension to timeless jewellery design. At House of Karnnikaraa, it is thoughtfully interpreted through silver jewellery — balancing luminosity, detail, and modern sophistication within the house’s heritage-inspired design language."
        },
        3: {
          title: "Eternal Moissanite",
          banner: "/banner_silver_jewelry.jpg",
          description: "Moissanite, admired for its exceptional brilliance and enduring beauty, brings a contemporary radiance to fine jewellery traditions. At House of Karnnikaraa, it is reimagined through sterling silver into timeless creations that balance luminosity, elegance, and modern sophistication while remaining rooted in the house’s cultural design sensibility."
        },
        4: {
          title: "Royal Meenakari",
          banner: "/new_arrival_3.jpg",
          description: "Meenakari, one of Rajasthan’s most celebrated decorative art forms, is known for its intricate enamel work, vivid colours, and royal heritage. House of Karnnikaraa reinterprets this timeless craft through silver jewellery — preserving its ornamental richness and cultural artistry within a contemporary language of adornment."
        },
        5: {
          title: "Jaipur Blue Pottery",
          banner: "/blue_pottery_banner.jpg",
          description: "Blue Pottery, one of Jaipur’s most treasured artistic legacies, reflects the richness of Rajasthan’s cultural heritage through its intricate motifs and luminous blue glaze. House of Karnnikaraa stands amongst the only jewellery houses to reinterpret this iconic Jaipur craft into silver jewellery — transforming a timeless art form into collectible wearable heritage."
        },
        6: {
          title: "Minimalist Luxe",
          banner: "/hero_silver_jewelry.jpg",
          description: "Minimalist Luxe reflects a quieter expression of adornment — where clean forms, subtle detailing, and refined silver craftsmanship come together with timeless versatility. Thoughtfully designed for the modern woman, the collection balances understated elegance with House of Karnnikaraa’s enduring sensibility towards culture, beauty, and everyday sophistication."
        }
      });

      return () => {
        if (typeof unsubscribeDiscounts === 'function') unsubscribeDiscounts();
        if (typeof unsubscribeHome === 'function') unsubscribeHome();
        if (typeof unsubscribeCollections === 'function') unsubscribeCollections();
      };
    };
    const cleanupSettingsPromise = fetchLiveDbData();
    return () => {
      if (typeof unsubscribeProducts === 'function') {
        unsubscribeProducts();
      }
      if (cleanupSettingsPromise && typeof cleanupSettingsPromise.then === 'function') {
        cleanupSettingsPromise.then(cleanup => {
          if (typeof cleanup === 'function') cleanup();
        });
      }
    };
  }, []);

  // Real-time stock restriction notification
  const [stockAlert, setStockAlert] = useState(null);

  useEffect(() => {
    let timer = null;
    const handleStockLimit = (e) => {
      const msg = e.detail?.message || "Due to high demand we can only fulfill this quantity for now.";
      setStockAlert(msg);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setStockAlert(null);
      }, 4500);
    };
    window.addEventListener('hok_stock_limit_alert', handleStockLimit);
    return () => {
      window.removeEventListener('hok_stock_limit_alert', handleStockLimit);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Manage navigation path tracking dynamically
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.toLowerCase();
      if (path === '/admin') {
        setView('admin');
      } else if (path === '/shipping-policy') {
        setView('legal');
        setLegalTitle('Shipping Policy');
      } else if (path === '/return-policy' || path === '/exchange-return-refund-policy' || path === '/refund-policy') {
        setView('legal');
        setLegalTitle('Exchange, Return & Refund Policy');
      } else if (path === '/terms-and-conditions' || path === '/terms') {
        setView('legal');
        setLegalTitle('Terms & Conditions');
      } else if (path === '/privacy-policy') {
        setView('legal');
        setLegalTitle('Privacy Policy');
      } else {
        setView('home');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Intercept global footer legal navigation links
  useEffect(() => {
    const handleLegalView = (e) => {
      const title = e.detail?.title || 'Terms & Conditions';
      setLegalTitle(title);
      setView('legal');
      let path = '/terms-and-conditions';
      if (title === 'Shipping Policy') path = '/shipping-policy';
      else if (title === 'Exchange, Return & Refund Policy' || title === 'Refund Policy') path = '/return-policy';
      else if (title === 'Privacy Policy') path = '/privacy-policy';
      window.history.pushState(null, '', path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('hok_legal_view', handleLegalView);
    return () => window.removeEventListener('hok_legal_view', handleLegalView);
  }, []);

  // Intercept global booking drawer open requests (e.g. from Footer Bespoke)
  useEffect(() => {
    const handleOpenBooking = () => {
      setIsBookingOpen(true);
    };
    window.addEventListener('hok_open_booking', handleOpenBooking);
    return () => window.removeEventListener('hok_open_booking', handleOpenBooking);
  }, []);

  const sharedProps = {
    view,
    setView,
    isMenuOpen,
    setIsMenuOpen,
    isCartOpen,
    setIsCartOpen,
    isBookingOpen,
    setIsBookingOpen,
    isSearchOpen,
    setIsSearchOpen,
    legalTitle,
    setLegalTitle,
    selectedCollectionId,
    setSelectedCollectionId,
    favorites,
    setFavorites,
    cartItems,
    setCartItems,
    bookingStep,
    setBookingStep,
    bookingForm,
    setBookingForm,
    selectedProduct,
    setSelectedProduct,
    selectedCategory,
    setSelectedCategory,
    currentUser,
    setCurrentUser,
    collectionsData,
    setCollectionsData,
    products,
    setProducts,
    discounts,
    setDiscounts,
  };

  // Safe view toggle helper that scrolls to top and handles pathname routing
  const handleSetView = (newView, titleParam) => {
    setView(newView);
    if (newView !== 'marketplace') {
      setSelectedCategory('All');
    }
    if (newView === 'admin') {
      window.history.pushState(null, '', '/admin');
    } else if (newView === 'legal') {
      const activeTitle = titleParam || legalTitle;
      let path = '/terms-and-conditions';
      if (activeTitle === 'Shipping Policy') path = '/shipping-policy';
      else if (activeTitle === 'Exchange, Return & Refund Policy' || activeTitle === 'Refund Policy') path = '/return-policy';
      else if (activeTitle === 'Privacy Policy') path = '/privacy-policy';
      window.history.pushState(null, '', path);
    } else {
      window.history.pushState(null, '', '/');
    }
    if (newView === 'collections' && sessionStorage.getItem('hok_skip_collection_hero') === 'true') {
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  window.setHokCategory = (cat) => {
    setSelectedCategory(cat);
  };

  const propsWithScroll = {
    ...sharedProps,
    setView: handleSetView,
  };

  return (
    <>
      {view === 'admin' ? (
        <AdminPanel 
          {...propsWithScroll} 
          products={products} 
          setProducts={setProducts} 
          discounts={discounts} 
          setDiscounts={setDiscounts} 
          homeContent={homeContent}
          setHomeContent={setHomeContent}
        />
      ) : view === 'newarrivals' ? (
        <NewArrivals {...propsWithScroll} />
      ) : view === 'payment' ? (
        <Payment {...propsWithScroll} />
      ) : view === 'checkout' ? (
        <Checkout {...propsWithScroll} />
      ) : view === 'collections' ? (
        <Collections {...propsWithScroll} products={products} setProducts={setProducts} />
      ) : view === 'legal' ? (
        <LegalPage {...propsWithScroll} title={legalTitle} setLegalTitle={setLegalTitle} />
      ) : view === 'marketplace' ? (
        <Marketplace {...propsWithScroll} products={products} />
      ) : view === 'product' ? (
        <ProductPage {...propsWithScroll} product={(products || []).find(p => String(p.id) === String(selectedProduct?.id)) || selectedProduct} products={products} />
      ) : (
        <Homepage {...propsWithScroll} homeContent={homeContent} />
      )}
      <SearchDrawer 
        isOpen={isSearchOpen} 
        onClose={() => setIsSearchOpen(false)} 
        products={products} 
        setCartItems={setCartItems} 
        setSelectedProduct={setSelectedProduct}
        setView={handleSetView}
      />

      {/* Luxury Floating Stock Limit Alert Toast */}
      {stockAlert && (
        <div className="fixed top-24 sm:top-24 left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[9999] max-w-md bg-[#584738] text-[#FAF8F5] border border-[#B59E7D]/60 p-3.5 sm:px-5 sm:py-3.5 rounded-md shadow-2xl flex items-center justify-between gap-3 animate-fade-in transition-all">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <svg className="w-5 h-5 text-[#B59E7D] shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <span className="text-xs sm:text-[13px] font-sans tracking-wide leading-snug font-medium break-words text-left">{stockAlert}</span>
          </div>
          <button 
            onClick={() => setStockAlert(null)} 
            className="p-1 text-white/60 hover:text-white text-base leading-none shrink-0 focus:outline-none cursor-pointer"
            aria-label="Dismiss stock alert"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
}

export default App;
